const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

/**
 * Format a number into a compact human-readable string.
 * 1234 -> "1.2K", 1234567 -> "1.2M"
 */
export function formatNumber(n: number): string {
  if (n >= 1_000_000) {
    const value = n / 1_000_000;
    return `${value % 1 === 0 ? value.toFixed(0) : value.toFixed(1)}M`;
  }
  if (n >= 1_000) {
    const value = n / 1_000;
    return `${value % 1 === 0 ? value.toFixed(0) : value.toFixed(1)}K`;
  }
  return n.toString();
}

/**
 * Format a date into relative time in Spanish.
 * "hace 2 dias", "hace 3 semanas", etc.
 */
export function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diff = now.getTime() - d.getTime();

  if (diff < 0) return 'ahora';

  if (diff < MINUTE) return 'hace unos segundos';
  if (diff < HOUR) {
    const mins = Math.floor(diff / MINUTE);
    return mins === 1 ? 'hace 1 minuto' : `hace ${mins} minutos`;
  }
  if (diff < DAY) {
    const hours = Math.floor(diff / HOUR);
    return hours === 1 ? 'hace 1 hora' : `hace ${hours} horas`;
  }
  if (diff < WEEK) {
    const days = Math.floor(diff / DAY);
    return days === 1 ? 'hace 1 dia' : `hace ${days} dias`;
  }
  if (diff < MONTH) {
    const weeks = Math.floor(diff / WEEK);
    return weeks === 1 ? 'hace 1 semana' : `hace ${weeks} semanas`;
  }
  if (diff < YEAR) {
    const months = Math.floor(diff / MONTH);
    return months === 1 ? 'hace 1 mes' : `hace ${months} meses`;
  }

  const years = Math.floor(diff / YEAR);
  return years === 1 ? 'hace 1 ano' : `hace ${years} anos`;
}

/**
 * Format a duration in seconds to "m:ss" format.
 * 225 -> "3:45"
 */
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
